import React, { useCallback, useEffect, useMemo, useState } from "react";
import Confetti from "react-confetti-boom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowRightIcon,
  Body2,
  Button,
  CheckboxBlankTogglerIcon,
  CompleteCheckbox,
  ConfirmDialog,
  Header2,
  Link,
  ProgressBar,
} from "@activecollab/components";
import classNames from "classnames";
import { showErrorToast } from "~/components/Toast";
import { AddYourTeamDialog } from "~/pages/GettingStarted/AddYourTeamDialog";
import BusinessInfoDialog from "~/pages/GettingStarted/BusinessInfoDialog";
import { CreateYourFirstProjectDialog } from "~/pages/GettingStarted/CreateYourFirstProjectDialog";
import ProjectLink from "~/pages/GettingStarted/ProjectLink";
import { SpecifyRateCardDialog } from "~/pages/GettingStarted/SpecifyRateCardDialog";
import StepLoader from "~/pages/GettingStarted/StepLoader";
import {
  useClaimRewardMutation,
  useGettingStartedSteps,
  useSetCelebrationShownMutation,
} from "~/repository/useGettingStarted";
import { getConfigOptions } from "~/store/actions/actions.configOptions";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import Angie from "~/utils/angie";

const GettingStartedSteps = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showClaimedRewardVersion, setShowClaimedRewardVersion] = useState(false);
  const { data: stepsData, isFetched: stepsFetched } = useGettingStartedSteps({
    onSuccess: (r) => {
      if (r?.reward_claimed) {
        setShowClaimedRewardVersion(true);
      }
    },
  });
  const claimRewardMutation = useClaimRewardMutation();
  const { mutate: setCelebrationShownMutation } = useSetCelebrationShownMutation();
  const [configOptionsFetched, setConfigOptionsFetched] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const showRewardVersion = useSelector((state) => getFeatureFlag(state, "onboarding_credit"));
  const [claimRewardDialogOpen, setClaimRewardDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(getConfigOptions(["time_first_week_day", "time_workdays", "weekly_worker_capacity"])).then(() =>
      setConfigOptionsFetched(true)
    );
  }, [dispatch]);

  const steps = useMemo(() => {
    return [
      {
        id: "getting_started_create_account",
        title: t("Create an Account"),
        desc: t("Get unrestricted access to everything ActiveCollab has to offer."),
        isCompleted: stepsData?.getting_started_create_account,
        logLabel: "create_account",
      },
      {
        id: "getting_started_setup_organization",
        title: t("Add Your Business Info"),
        desc: t("Manage your budgets and financial documents more effectively."),
        isCompleted: stepsData?.getting_started_setup_organization,
        logLabel: "organization_setup",
      },
      {
        id: "getting_started_specify_rate_card",
        title: t("List Your Services and Rates"),
        desc: t("Use rate cards for quotes, invoicing, workload management, and analytics."),
        isCompleted: stepsData?.getting_started_specify_rate_card,
        logLabel: "specify_rate_card",
      },
      {
        id: "getting_started_setup_team",
        title: t("Add Your Team"),
        desc: t("Set up team accounts, including roles and permissions, before inviting them."),
        isCompleted: stepsData?.getting_started_setup_team,
        logLabel: "setup_your_team",
      },
      {
        id: "getting_started_project",
        title: t("Create Project"),
        desc: t("Explore your workspace or let our AI help you get some hands-on experience."),
        isCompleted: stepsData?.getting_started_project,
        logLabel: "create_first_project",
      },
    ];
  }, [stepsData, t]);

  const numOfCompletedSteps = useMemo(() => {
    return steps.reduce((acc, step) => {
      return step.isCompleted ? ++acc : acc;
    }, 0);
  }, [steps]);

  const numOfSteps = useMemo(() => steps?.length, [steps]);

  const percent = useMemo(() => {
    return (numOfCompletedSteps / numOfSteps) * 100;
  }, [numOfCompletedSteps, numOfSteps]);

  const isCompleted = useMemo(() => numOfCompletedSteps === numOfSteps, [numOfCompletedSteps, numOfSteps]);

  const closeDialog = useCallback(
    (shouldLog = true) => {
      if (shouldLog) {
        const openedStep = steps.find((step) => step.id === openDialog);
        Angie.logger.info(
          "Getting started step abandoned.",
          { step: openedStep?.logLabel, reopen: openedStep?.isCompleted ? 1 : 0 },
          true
        );
      }
      setOpenDialog(false);
    },
    [openDialog, steps]
  );

  const openConfirmClaimRewardDialog = useCallback(() => setClaimRewardDialogOpen(true), [setClaimRewardDialogOpen]);
  const closeConfirmClaimRewardDialog = useCallback(() => setClaimRewardDialogOpen(false), [setClaimRewardDialogOpen]);
  const onClaimReward = useCallback(() => {
    claimRewardMutation.mutateAsync().then((r) => {
      if (r?.is_ok) {
        closeConfirmClaimRewardDialog();
        return setShowClaimedRewardVersion(true);
      }

      closeConfirmClaimRewardDialog();
      showErrorToast("There was an error, please try again.");
    });
  }, [claimRewardMutation, closeConfirmClaimRewardDialog]);

  useEffect(() => {
    if (numOfCompletedSteps === numOfSteps && !stepsData?.celebration_shown) {
      setTimeout(() => {
        setCelebrationShownMutation();
        setShowCelebration(true);
      }, 500);
    }
  }, [numOfCompletedSteps, numOfSteps, setCelebrationShownMutation, stepsData?.celebration_shown]);

  return (
    <div
      className="tw-bg-theme-200 tw-p-4"
      data-testid="getting-started-steps"
    >
      {!stepsFetched || !configOptionsFetched ? (
        <StepLoader />
      ) : showClaimedRewardVersion && showRewardVersion ? (
        <div
          className={classNames("tw-flex tw-flex-col tw-h-full", {
            "tw-justify-between": projectId,
            "tw-justify-center": !projectId,
          })}
        >
          <div className="tw-flex tw-flex-col tw-items-center">
            <img
              src={`${Angie.assets_url}/onboarding/images/Reward-rocket.png`}
              alt="Reward image"
            />
            <Header2>
              {t("Your")} ${stepsData?.reward} {t("credit is waiting")}!
            </Header2>
            <Body2 className="tw-mt-2">
              {t("Activate your plan, and start getting the most out of ActiveCollab.")}{" "}
            </Body2>
            <RouterLink to="/subscription/choose-plan">
              <Button className="tw-mt-4">{t("Choose your plan")}</Button>
            </RouterLink>
          </div>
          {projectId ? (
            <ProjectLink
              id={projectId}
              className="tw-mt-4 lg:tw-mt-0"
            />
          ) : null}
        </div>
      ) : (
        <>
          {showRewardVersion ? (
            <>
              {showCelebration ? (
                <Confetti
                  particleCount={500}
                  x={0.5}
                  y={0}
                  spreadDeg={180}
                  colors={[
                    "#FF595E", // bright red-pink
                    "#FFCA3A", // warm yellow
                    "#8AC926", // lime green
                    "#1982C4", // sky blue
                    "#6A4C93", // purple
                    "#FF9F1C", // orange
                    "#2EC4B6", // teal
                    "#E71D36", // red
                  ]}
                />
              ) : null}
              <div
                className={classNames(
                  "tw-flex tw-gap-4 tw-justify-between tw-border tw-border-solid tw-border-border-primary tw-rounded-lg tw-py-3 tw-pl-6",
                  {
                    "tw-bg-primary tw-cursor-pointer": isCompleted,
                    "tw-pointer-events-none": !isCompleted,
                  }
                )}
                onClick={openConfirmClaimRewardDialog}
              >
                <div className="">
                  <Header2 {...(isCompleted && { color: "page-paper" })}>
                    {numOfCompletedSteps < numOfSteps
                      ? t("Get Started with ActiveCollab")
                      : t("Congrats, you won $") + stepsData?.reward ?? 50 + "!"}
                  </Header2>
                  {!isCompleted ? (
                    <Body2 className="tw-mt-1">
                      {t("Complete all 5 steps and")}{" "}
                      <b>
                        {t("win")} ${stepsData?.reward ?? 50}
                      </b>{" "}
                      {t("toward your subscription")}!
                    </Body2>
                  ) : (
                    <Body2
                      className="tw-mt-1"
                      color="page-paper"
                    >
                      {t("Save as much on your ActiveCollab plan")}!
                    </Body2>
                  )}
                  {!isCompleted ? (
                    <div className="tw-flex tw-gap-4 tw-items-center tw-mt-2">
                      <ProgressBar
                        progress={percent}
                        stroke={8}
                        className="tw-rounded tw-overflow-hidden"
                      />
                      <Body2>{percent}%</Body2>
                    </div>
                  ) : (
                    <Body2
                      color="page-paper"
                      className="tw-mt-2"
                    >
                      {t("Click to Claim Your Reward")}
                    </Body2>
                  )}
                </div>
                <svg
                  width="62"
                  height="83"
                  viewBox="0 0 62 83"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M79.619 29.746L87.1828 58.5422C88.2272 62.5181 85.8506 66.5879 81.8747 67.6322L24.2822 82.7597C20.3063 83.8041 16.2366 81.4276 15.1922 77.4516L7.62846 48.6554C5.6405 49.1776 3.60563 47.9893 3.08346 46.0013L0.247047 35.2028C-0.797292 31.2268 1.57923 27.1571 5.55516 26.1128L16.9657 23.1156C16.2766 22.1038 15.7392 20.9367 15.4083 19.6768C13.8418 13.7129 17.4066 7.60833 23.3705 6.04182C26.97 5.09635 30.6103 6.0641 33.2897 8.20774L33.2802 8.17174L36.06 10.4045L37.3837 7.09391L37.3932 7.1299C38.6732 3.94624 41.3681 1.31446 44.9676 0.368989C50.9315 -1.19752 57.0361 2.36727 58.6026 8.33116C58.9335 9.59099 59.039 10.8716 58.9362 12.0914L70.3467 9.09427C74.3226 8.04993 78.3923 10.4265 79.4367 14.4024L82.2731 25.201C82.7953 27.1889 81.607 29.2238 79.619 29.746ZM22.3913 75.5607L47.588 68.9424L40.0242 40.1462L14.8275 46.7645L22.3913 75.5607ZM79.9838 60.4331L72.42 31.6369L47.2233 38.2552L54.7871 67.0514L79.9838 60.4331ZM25.2614 13.2409C23.2734 13.763 22.0852 15.7979 22.6073 17.7859C23.1295 19.7738 25.1644 20.9621 27.1523 20.4399C29.1403 19.9178 30.3286 17.8829 29.8064 15.8949C29.2842 13.907 27.2494 12.7187 25.2614 13.2409ZM46.8586 7.56805C44.8706 8.09022 43.6823 10.1251 44.2045 12.113C44.7267 14.101 46.7615 15.2893 48.7495 14.7671C50.7375 14.2449 51.9257 12.2101 51.4036 10.2221C50.8814 8.23414 48.8465 7.04588 46.8586 7.56805ZM7.44611 33.3118L9.33705 40.5109L38.1333 32.9471L36.2423 25.748L7.44611 33.3118ZM43.4414 23.8571L45.3323 31.0562L74.1286 23.4924L72.2376 16.2933L43.4414 23.8571Z"
                    fill={isCompleted ? "var(--color-primary-800)" : "var(--color-theme-400)"}
                  />
                </svg>
              </div>
            </>
          ) : (
            <>
              <Header2>{t("Get Started with ActiveCollab")}</Header2>
              <ProgressBar
                progress={percent}
                stroke={8}
                className="tw-mt-3 tw-rounded tw-overflow-hidden"
              />
              <Body2 className="tw-mt-1">{`${numOfCompletedSteps} of ${numOfSteps} steps completed`}</Body2>
            </>
          )}
          <div className="tw-flex tw-flex-col tw-gap-4 tw-overflow-hidden tw-mt-8 tw-relative after:tw-content-[''] after:tw-absolute after:tw-bg-theme-400 after:tw-h-full after:tw-w-px after:tw-left-3 after:tw-z-0">
            {steps.map((step, i) => {
              return (
                <div
                  className={classNames("tw-flex tw-gap-2 tw-items-center tw-relative", {
                    "before:tw-content-[''] before:tw-absolute before:tw-bg-theme-200 before:tw-w-[20px] before:tw-h-1/2 before:tw-z-10 before:tw-top-0":
                      i === 0,
                    "after:tw-content-[''] after:tw-absolute after:tw-bg-theme-200 after:tw-w-[20px] after:tw-h-1/2 after:tw-z-10 after:tw-bottom-0":
                      i === numOfSteps - 1,
                  })}
                  key={step.id}
                >
                  {step.isCompleted ? (
                    <div className="tw-bg-theme-200 tw-z-20 tw-w-6 tw-h-[28px] tw-flex tw-items-center">
                      <CompleteCheckbox
                        completed
                        className="tw-shrink-0 tw-pointer-events-none tw-z-20"
                      />
                    </div>
                  ) : (
                    <div className="tw-bg-theme-200 tw-w-6 tw-h-6 tw-z-20">
                      <CheckboxBlankTogglerIcon className="tw-shrink-0" />
                    </div>
                  )}
                  <div
                    className={classNames("tw-p-3 tw-w-full tw-flex tw-justify-between tw-items-center tw-rounded-lg", {
                      "hover:tw-bg-theme-300 tw-cursor-pointer": step.id !== "getting_started_create_account",
                      "tw-pointer-events-none": step.id === "getting_started_create_account",
                    })}
                    onClick={() => {
                      Angie.logger.info(
                        "Getting started step clicked.",
                        { step: step.logLabel, reopen: step.isCompleted ? 1 : 0 },
                        true
                      );
                      setOpenDialog(step.id);
                    }}
                  >
                    <div>
                      <Body2
                        weight="bold"
                        color="theme-primary"
                      >
                        {`${++i}. ${step.title}`}
                      </Body2>
                      <Body2 color="tertiary">{step.desc}</Body2>
                    </div>
                    {step.id !== "getting_started_create_account" ? <ArrowRightIcon /> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {configOptionsFetched && openDialog === "getting_started_setup_organization" ? (
        <BusinessInfoDialog
          open={true}
          onClose={closeDialog}
          isAlreadyCompleted={steps[1].isCompleted}
        />
      ) : null}
      {openDialog === "getting_started_specify_rate_card" ? (
        <SpecifyRateCardDialog
          open={true}
          onClose={closeDialog}
          isAlreadyCompleted={steps[2].isCompleted}
        />
      ) : null}
      {configOptionsFetched && openDialog === "getting_started_setup_team" ? (
        <AddYourTeamDialog
          open={true}
          onClose={closeDialog}
          isAlreadyCompleted={steps[3].isCompleted}
        />
      ) : null}
      {openDialog === "getting_started_project" ? (
        <CreateYourFirstProjectDialog
          open={true}
          onClose={closeDialog}
          isAlreadyCompleted={steps[4].isCompleted}
          setProjectId={setProjectId}
        />
      ) : null}
      {claimRewardDialogOpen ? (
        <ConfirmDialog
          open={claimRewardDialogOpen}
          onClose={closeConfirmClaimRewardDialog}
          onConfirm={onClaimReward}
          dialogTitle={t("Claim Your Reward")}
          dialogContent={
            <div className="tw-flex tw-gap-4 tw-flex-col">
              <Body2 weight="bold">{t("You did it — all 5 steps complete")}! 🎉</Body2>
              <Body2>
                {t("To say thanks, we're offering $")}
                {stepsData?.reward ?? 50},{" "}
                {t(
                  "which you can use to purchase or renew your ActiveCollab plan. Once claimed, the credit will be added to your account, and stay there until it's all used up"
                )}
                .
              </Body2>
              <Body2>
                {t("Got a question? Our Customer Success team is here to help via chat or email at")}
                &nbsp;
                <Link
                  variant="text link-primary"
                  href="mailto:support@activecollab.com"
                >
                  support@activecollab.com
                </Link>
                .
              </Body2>
            </div>
          }
          confirmBtnText={t("Claim Your $") + (stepsData?.reward ?? 50) + " " + t("credit")}
          onCancel={closeConfirmClaimRewardDialog}
        />
      ) : null}
    </div>
  );
};

export default GettingStartedSteps;
